import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "intouch.admin.token";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [user, setUser] = useState(null);
  const [bootChecked, setBootChecked] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Verify existing token on mount
  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!token) {
        setBootChecked(true);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setUser(data);
      } catch (e) {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setBootChecked(true);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async ({ username, password, security_answer }) => {
    const { data } = await axios.post(`${API}/admin/login`, {
      username,
      password,
      security_answer,
    });
    localStorage.setItem(STORAGE_KEY, data.token);
    setToken(data.token);
    setUser({ username: data.username });
    return data;
  }, []);

  const authedClient = useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use((cfg) => {
      const t = localStorage.getItem(STORAGE_KEY);
      if (t) cfg.headers = { ...cfg.headers, Authorization: `Bearer ${t}` };
      return cfg;
    });
    inst.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return inst;
  }, []);

  const value = useMemo(
    () => ({ token, user, login, logout, bootChecked, authedClient }),
    [token, user, login, logout, bootChecked, authedClient]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
